import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ConfirmationService} from "primeng/api";
import {ObjectBaseComponent} from "../../../../../core/shared/object/mvs-object-base/object-base.component";
import {MvsCoreService} from "../../../../../core/shared/service/mvs-core.service";
import {MvsMessageService} from "../../../../../core/shared/service/message/mvs-message.service";
import {ObserverService} from "../../../../../core/shared/object/service/observer.service";
import {NavigationItem} from "../../../../../core/shared/dto/navigation/navigation-item";
import {WidgetData} from "../../../../../core/shared/widget/widget-data";
import {WidgetFactory} from "../../../../../core/shared/widget/service/widget.factory";
import {FilterCriteria} from "../../../../../core/shared/filter/api/filter.criteria";
import {Sorting} from "../../../../../core/shared/misc/sorting";
import {WidgetToolbarCallbackInterface} from "../../../../../core/shared/widget/widget-toolbar-callback.interface";
import {DtoDetail} from "../../../../../core/shared/dto/dto.detail";
import {MvsFormControlOverwrite} from "../../../../../core/shared/logic/form/logic/mvs-form-control-overwrite";
import {MvsFormFieldAccessEnum} from "../../../../../core/shared/logic/form/logic/mvs-form-field-access.enum";
import {ActivatedRoute} from "@angular/router";
import {Subscription} from "rxjs";

@Component({
    selector: 'mvs-am-agent-object',
    templateUrl: './agent-object.component.html',
    styleUrls: ['./agent-object.component.scss'],
    standalone: false
})
export class AgentObjectComponent
    extends ObjectBaseComponent
    implements OnInit, OnChanges, OnDestroy {

    activeNavigationItem: NavigationItem;
    widgetAgentData: WidgetData;
    widgetAgentHolidayCalendar: WidgetData;
    widgetAgentPoolAgent: WidgetData;
    widgetAgentScheduleDay: WidgetData;
    widgetAgentScheduleWeek: WidgetData;
    widgetAgentNumber: WidgetData;
    private queryParamSubscription: Subscription;

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService,
        protected override observerService: ObserverService,
        private route: ActivatedRoute
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }

    ngOnInit() {
        super.ngOnInit();
        this.subscribeToNavItemQueryParam();
    }

    onObjectChanged() {
        this.setPageTitle(this.dto['name'] || 'Agent');
        this.initNavigationItems();
        this.refreshWidgets();
    }

    initNavigationItems() {
        const navigationItems: NavigationItem[] = [
            {label: '\u00dcbersicht', action: 'overview', icon: 'fa-regular fa-id-card', tooltip: 'Agentendaten'},
            {label: 'Feiertagskalender', action: 'agentHolidayCalendar', icon: 'fa-regular fa-calendar-star', tooltip: 'Zugewiesene Feiertagskalender'},
            {label: 'Poolzuweisungen', action: 'agentPoolAgent', icon: 'fa-regular fa-users', tooltip: 'Zugewiesene Agentenpools'},
            {label: 'Arbeitszeitwoche', action: 'agentScheduleWeek', icon: 'fa-regular fa-calendar-week', tooltip: 'Regul\u00e4re Arbeitszeit Woche'},
            {label: 'Arbeitszeitdetail', action: 'agentScheduleDay', icon: 'fa-regular fa-clock', tooltip: 'Arbeitszeit pro Tag'},
            {label: 'Externe Nummern', action: 'agentNumber', icon: 'fa-regular fa-hashtag', tooltip: 'Zugewiesene externe Nummern (Vermittlernummer, Registrierungsnummer)'}
        ];
        if (!this.activeNavigationItem) {
            this.activeNavigationItem = navigationItems[0];
        }
        this.onNavigationItems.emit(navigationItems);
    }

    handleNavigationItemChange(navigationItem: NavigationItem) {
        this.activeNavigationItem = navigationItem;
    }

    refreshWidgets() {
        this.refreshAgentDataWidget();
        this.refreshAgentHolidayCalendarWidget();
        this.refreshAgentPoolAgentWidget();
        this.refreshAgentScheduleWeekWidget();
        this.refreshAgentScheduleDayWidget();
        this.refreshAgentNumberWidget();
    }

    refreshAgentDataWidget() {
        this.widgetAgentData = WidgetFactory.createWidgetEntityData(
            'am.agent.object.data', 'Agentendaten', 'am.Agent', this.objectIdentifier.objectId
        );
    }

    refreshAgentHolidayCalendarWidget() {
        this.widgetAgentHolidayCalendar = WidgetFactory.createWidgetListEntityBasicQl(
            'am.agent.object.holiday.calendar', 'Feiertagskalender', 'am.AgentHolidayCalendar',
            [FilterCriteria.create('e.agentId', FilterCriteria.cOperatorEqual, this.objectIdentifier.objectId)],
            [new Sorting('e.id', false)], true
        );
        this.widgetAgentHolidayCalendar.functionCallbacks = this.createChildToolbarCallbacks('agentDtoId');
    }

    refreshAgentPoolAgentWidget() {
        this.widgetAgentPoolAgent = WidgetFactory.createWidgetListEntityBasicQl(
            'am.agent.object.pool.agent', 'Poolzuweisungen', 'am.AgentPoolAgent',
            [FilterCriteria.create('e.agentId', FilterCriteria.cOperatorEqual, this.objectIdentifier.objectId)],
            [new Sorting('e.id', false)], true
        );
        this.widgetAgentPoolAgent.functionCallbacks = this.createChildToolbarCallbacks('agentDtoId');
    }

    refreshAgentScheduleWeekWidget() {
        this.widgetAgentScheduleWeek = WidgetFactory.createWidgetListEntityBasicQl(
            'am.agent.object.schedule.week', 'Arbeitszeitwoche', 'am.AgentScheduleWeek',
            [FilterCriteria.create('e.agentId', FilterCriteria.cOperatorEqual, this.objectIdentifier.objectId)],
            [new Sorting('e.id', false)], true
        );
        this.widgetAgentScheduleWeek.functionCallbacks = this.createChildToolbarCallbacks('agentDtoId');
    }

    refreshAgentScheduleDayWidget() {
        this.widgetAgentScheduleDay = WidgetFactory.createWidgetListEntityBasicQl(
            'am.agent.object.schedule.day', 'Arbeitszeitdetail', 'am.AgentScheduleDay',
            [FilterCriteria.create('e.agentId', FilterCriteria.cOperatorEqual, this.objectIdentifier.objectId)],
            [new Sorting('e.id', false)], true
        );
        this.widgetAgentScheduleDay.functionCallbacks = this.createChildToolbarCallbacks('agentDtoId');
    }

    refreshAgentNumberWidget() {
        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Table of the external numbers held by this agent per partner (Vermittlernummer, IHK Registrierungsnummer); new entries are created via the widget menu ("Anlegen"), editing and deletion via the row context menu
         */
        this.widgetAgentNumber = WidgetFactory.createWidgetTableEntityBasicQl(
            'am.agent.object.number', 'Externe Nummern', 'am.AgentNumber',
            [FilterCriteria.create('e.agentId', FilterCriteria.cOperatorEqual, this.objectIdentifier.objectId)],
            [new Sorting('e.id', false)], true
        );
        this.widgetAgentNumber.functionCallbacks = this.createChildToolbarCallbacks('agentDtoId');
    }

    createChildToolbarCallbacks(fkFieldName: string): any {
        return {
            onFunctionCreateNew: (widgetData: WidgetData): WidgetToolbarCallbackInterface => {
                const dto = new DtoDetail();
                dto[fkFieldName] = this.objectIdentifier.objectId;
                const formControlOverwrite = new MvsFormControlOverwrite();
                formControlOverwrite.addField(fkFieldName, MvsFormFieldAccessEnum.READ);
                return {dto, formControlOverwrite};
            }
        };
    }

    private subscribeToNavItemQueryParam(): void {
        this.queryParamSubscription = this.route.queryParams.subscribe(params => {
            const navItemAction = params['navItem'];
            if (navItemAction && this.onNavigationItems) {
                this.activeNavigationItem = {action: navItemAction} as NavigationItem;
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        if (!this.initialized) { return; }
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.queryParamSubscription?.unsubscribe();
    }
}
